import { useNavigate } from 'react-router-dom';
import { formatRelativeAge } from '../../lib/format';
import { safeExternalUrl } from '../../lib/url';
import type { NewsItemRead } from '../../types/api';
import { GENERATION_PATH } from '../generation/generationPrefill';
import { newsItemPrefill, newsSourceName } from './newsPrefill';

interface NewsCardProps {
  item: NewsItemRead;
  onPlan: (item: NewsItemRead) => void;
}

/**
 * One article off the industry feed, as a compact row: publication and age on
 * one line, the headline, a two-line snippet, and the actions on the right.
 *
 * Everything on this row is somebody else's text, so every field is rendered
 * as a string and nothing is ever handed to `dangerouslySetInnerHTML`. The link
 * is only drawn when the URL parses as http(s) — see `lib/url.ts`.
 *
 * Three actions, all quiet: a list of twenty rows with twenty filled buttons
 * has no lead action at all. **Generate post** is the strongest of the three
 * (outlined, the others ghost): it carries the article to the brief at
 * `/generation` as router state (`newsItemPrefill`). It costs nothing here —
 * the quota is spent when that form is submitted. It is only offered when the
 * URL is linkable, since an article the research node cannot fetch is nothing
 * to ground a post on. The other two spend nothing at all: plan a post off it,
 * or go read it.
 */
export function NewsCard({ item, onPlan }: NewsCardProps) {
  const navigate = useNavigate();
  const href = safeExternalUrl(item.url);
  const prefill = newsItemPrefill(item);

  return (
    <li className="news-card">
      <div className="news-card__main">
        <p className="news-card__head">
          <span className="news-card__source">{newsSourceName(item)}</span>
          {/* No date on the item means no age on the row. "Unknown" would be a
              fact about the feed, and the reader is not here for that. */}
          {item.published_at ? (
            <span className="news-card__age">{formatRelativeAge(item.published_at)}</span>
          ) : null}
        </p>

        <h2 className="news-card__title">{item.title}</h2>
        {item.snippet ? <p className="news-card__snippet">{item.snippet}</p> : null}
      </div>

      <div className="news-card__actions">
        {prefill ? (
          <button
            className="button button--small"
            type="button"
            onClick={() => void navigate(GENERATION_PATH, { state: prefill })}
          >
            Create post
          </button>
        ) : null}
        <button
          className="button button--ghost button--small"
          type="button"
          onClick={() => onPlan(item)}
        >
          Plan post
        </button>
        {href ? (
          <a
            className="button button--ghost button--small"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open article
          </a>
        ) : null}
      </div>
    </li>
  );
}
